import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { GoogleMap, MarkerF, MarkerClustererF, LoadScript } from '@react-google-maps/api';
import { Container, CircularProgress, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectnodess } from '../redux/nodesSlice';
import { useGetMapOrganizationsQuery, useLazyGetOrganizationByIdQuery } from '../redux/organizationApi';
import OrganizationModal from './OrganizationModal';
import { mapAPIKey } from '../config/config';

// Move libraries array outside component to prevent re-renders
const GOOGLE_MAPS_LIBRARIES = ['places'];

const mapContainerStyle = {
  height: "100vh", 
  width: "100%",   
  position: "relative" 
};

const center = {
  lat: 31.5204,
  lng: 74.3587
};

const Orgsmap = () => {
  // Use RTK Query to fetch map organizations
  const { data: mapOrganizations = [], isLoading, isError } = useGetMapOrganizationsQuery();
  // Lazy query for fetching organization details by ID
  const [getOrganizationById, { data: organizationData, isLoading: isLoadingOrg, isError: isErrorOrg }] = useLazyGetOrganizationByIdQuery();
  // const allorganization = useSelector(selectnodess);
  const [open, setOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  // Use the data from RTK Query (already filtered and optimized from backend)
  const all = useMemo(() => {
    return mapOrganizations || [];
  }, [mapOrganizations]);

  // Memoize marker positions calculation
  const markers = useMemo(() => {
    if (!all || all.length === 0) {
      return [];
    }

    // Group by precise coordinates (rounded to 5 decimals to catch near-identical points)
    const groups = new Map();
    all?.forEach((company) => {
      const lat = company?.data?.location?.lat;
      const lng = company?.data?.location?.lng;

      if (typeof lat !== 'number' || typeof lng !== 'number') return;
      const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(company);
    });

    const adjusted = [];
    groups?.forEach((companies, key) => {
      // If single marker at this point, keep as is
      if (companies.length === 1) {
        adjusted.push({
          company: companies[0],
          position: {
            lat: companies[0]?.data?.location?.lat,
            lng: companies[0]?.data?.location?.lng,
          },
        });
        return;
      }

      // Spread multiple markers around the original point in a small circle
      const [latStr, lngStr] = key.split(',');
      const baseLat = parseFloat(latStr);
      const baseLng = parseFloat(lngStr);

      const count = companies.length;
      const radiusDeg = 0.00035; // ~39m; tweak as needed

      for (let i = 0; i < count; i++) {
        const angle = (2 * Math.PI * i) / count;
        // Adjust radius slightly with count to reduce overlap
        const r = radiusDeg * (1 + (count > 6 ? 0.5 : 0));
        const dLat = r * Math.cos(angle);
        // Account for longitude degree scaling with latitude
        const lngScale = Math.cos((baseLat * Math.PI) / 180) || 1;
        const dLng = (r * Math.sin(angle)) / lngScale;
        adjusted.push({
          company: companies[i],
          position: {
            lat: baseLat + dLat,
            lng: baseLng + dLng,
          },
        });
      }
    });

    return adjusted;
  }, [all]);

  // Memoize container style
  const containerStyle = useMemo(() => ({
    height: '100%', 
    width: '100%',   
    padding: 0,
  }), []);

  // Fetch organization data when selectedOrgId changes
  useEffect(() => {
    if (selectedOrgId) {
      getOrganizationById(selectedOrgId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  // Update open state when organization data is fetched
  useEffect(() => {
    if (organizationData && organizationData !== null) {
      setOpen(true);
    } else if (organizationData === null) {
      console.warn('Organization data is null');
      setOpen(false);
    }
  }, [organizationData]);

  // Handle errors when fetching organization
  useEffect(() => {
    if (isErrorOrg) {
      console.error('Error fetching organization details');
      setOpen(false);
      setSelectedOrgId(null);
      // Optionally show a toast or error message
    }
  }, [isErrorOrg]);

  // Memoize marker click handler
  const handleMarkerClick = useCallback((company) => {
    // Ensure company has a valid _id before setting
    if (company && company._id) {
      setSelectedOrgId(company._id);
    } else {
      console.warn('Marker clicked but company data is invalid:', company);
    }
  }, []);

  // Memoize modal close handler
  const handleModalClose = useCallback(() => {
    setOpen(false);
    setSelectedOrgId(null);
  }, []);

  // Show loading state
  if (isLoading) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        style={containerStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Loading map data...
        </div>
      </Container>
    );
  }

  // Show error state
  if (isError) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        style={containerStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Error loading map data. Please try again.
        </div>
      </Container>
    );
  }

  // Check if map API key is available
  if (!mapAPIKey) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        style={containerStyle}
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          Error: Google Maps API key is not configured
        </div>
      </Container>
    );
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      style={containerStyle}
    >
      <LoadScript
        googleMapsApiKey={mapAPIKey}
        libraries={GOOGLE_MAPS_LIBRARIES}
        loadingElement={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
          </Box>
        }
        onError={(error) => {
          console.error('Error loading Google Maps script:', error);
        }}
      >
        {markers && markers.length > 0 && (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={4}
            center={center}
            options={{
              disableDefaultUI: false,
              zoomControl: true,
            }}
          >
            <MarkerClustererF
              options={{
                gridSize: 60,
                maxZoom: 15,
              }}
            >
              {(clusterer) => {
                // Filter out invalid markers before rendering
                const validMarkers = markers?.filter(({ company, position }) => 
                  company && 
                  position && 
                  typeof position.lat === 'number' && 
                  typeof position.lng === 'number' &&
                  !isNaN(position.lat) &&
                  !isNaN(position.lng) &&
                  isFinite(position.lat) &&
                  isFinite(position.lng)
                ) || [];
                
                if (validMarkers.length === 0) {
                  return null;
                }
                
                return (
                  <>
                    {validMarkers.map(({ company, position }, index) => {
                      const companyName = company?.data?.companyName || company?.companyName || 'Unknown';
                      
                      return (
                        <MarkerF
                          key={company?._id || `marker-${index}`}
                          position={position}
                          clusterer={clusterer}
                          onClick={() => handleMarkerClick(company)}
                          label={{
                            text: companyName.substring(0, 20), // Limit label length
                            color: 'green',
                            fontSize: '12px',
                            fontWeight: 'bold',
                          }}
                          options={{
                            title: companyName,
                          }}
                        />
                      );
                    })}
                  </>
                );
              }}
            </MarkerClustererF>
          </GoogleMap>
        )}
      </LoadScript>
      {/* {isLoadingOrg && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </Box>
      )} */}
      <OrganizationModal
        open={open && !isLoadingOrg && organizationData !== null && organizationData !== undefined}
        onClose={handleModalClose}
        organization={organizationData}
        // allorg={allorganization}
      />
    </Container>
  );
};

export default Orgsmap;

